<template>
  <Body class="page-view page-review-view" />

  <!-- post header -->
  <section class="section post-header">
    <div class="wrap">
      <h1 class="post-title">
        {{ news?.title }}
      </h1>
      <div class="post-info">
        <div class="detail">
          <div v-if="news?.createdAt" class="date">
            {{ formatIntlDate(new Date(news.createdAt), locale) }}
          </div>
          <div class="view">
            <i class="ico ico-smile" />{{ formatNumber(news?.viewCnt ?? 0, locale) }}
          </div>
          <div class="comment">
            <i class="ico ico-reply" />{{ formatNumber(news?.commentCnt ?? 0, locale) }}
          </div>
          <div class="like">
            <i class="ico ico-heart-round" />{{ formatNumber(news?.likeCnt ?? 0, locale) }}
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- photo slide (이미지가 있으면 스와이프, 없으면 썸네일 한 장) -->
  <section v-if="news && (news.files.length > 0 || news.thumbnailUrl)" class="section photo-slide">
    <div class="wrap">
      <ClientOnly v-if="news.files.length > 0">
        <Swiper
          :modules="[Pagination, Autoplay]"
          :slides-per-view="1"
          :space-between="8"
          :autoplay="false"
          :auto-height="true"
          :pagination="{ el: '.photo-slide .wrap .dots', clickable: true }"
        >
          <SwiperSlide v-for="file in news.files" :key="file.id">
            <div class="item">
              <img :src="file.ws2_file_url" alt="">
            </div>
          </SwiperSlide>
        </Swiper>
        <div v-if="news.files.length > 1" class="dots" />
      </ClientOnly>
      <div v-else class="item">
        <img :src="news.thumbnailUrl!" alt="">
      </div>
    </div>
  </section>

  <!-- post contents -->
  <div class="section post-contents">
    <div class="wrap">
      <div class="contents-container we-contents" v-html="news?.contents" />

      <!-- likes -->
      <div class="like-wrap">
        <button type="button" class="btn-like" @click="onClickLikeButton">
          <ClientOnly>
            <Vue3Lottie
              ref="lottie"
              :animation-data="likeJSON"
              :speed="1"
              :auto-play="false"
              :loop="false"
              @on-animation-loaded="onAnimationLoaded"
            />
          </ClientOnly>
        </button>
        <div class="like-count">
          {{ formatNumber(news?.likeCnt ?? 0, locale) }}
        </div>
      </div>
    </div>
  </div>

  <!-- comment -->
  <div v-if="news && commentToken" class="section">
    <div class="wrap">
      <Comment :comment-token="commentToken" />
    </div>
  </div>

  <!-- btns -->
  <div class="section">
    <div class="wrap">
      <div class="btns">
        <a class="btn" @click="$router.back()">{{ $t('뒤로가기') }}</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// lottie
import { Vue3Lottie } from 'vue3-lottie'
import likeJSON from '@/assets/lotties/like.json'

import { Autoplay, Pagination } from 'swiper/modules'
// Swiper Slide
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/pagination'

interface NewsDetail {
  id: number
  thumbnailUrl: string | null
  title: string
  contents: string
  createdAt: string
  files: { id: number, ws2_file_url: string }[]
  viewCnt: number
  likeCnt: number
  commentCnt: number
  isLiked: boolean
}

const { $t } = useNuxtApp()
const { locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { isLoggedIn, showLoginPopup } = useAuth()

const { data: news, refresh } = await useFetch<NewsDetail>(`/api/news/${route.params.id}`, {
  method: 'GET',
  query: { languageGbSn: locale.value },
})

// 글이 없거나(해당 언어 번역 없음 포함) 아직 게시 전이면 후기와 같이 목록으로 이동한다
if (!news.value) {
  await navigateTo(localePath('/news'))
}

// 댓글 토큰 — 백엔드 /hml/v1/news/{id}/comment-token (받지 못하면 댓글 영역을 숨긴다)
const { fetchNewsCommentToken } = useReview()
const { data: commentToken } = await fetchNewsCommentToken(Number(route.params.id))

useHead({
  title: `${news.value?.title ?? ''} - ${$t('어머니사랑과평화의날캠페인')}`,
  link: [
    { rel: 'stylesheet', href: `/css/review.css?${useBuildTime()}` },
    { rel: 'stylesheet', href: 'https://pic.watv.org/watveditor/watvEditor@next.css' },
  ],
})

const lottie = ref<any>(null)

// 조회수 증가 — 하루 1회(후기와 동일하게 쿠키로 중복 방지)
onMounted(async () => {
  if (!news.value)
    return

  const tomorrow = new Date()
  tomorrow.setDate(new Date().getDate() + 1)
  const viewCookie = useCookie<number[]>('HMLNewsViewIds', { expires: tomorrow, default: () => [] })
  const id = Number(route.params.id)
  if (viewCookie.value.includes(id))
    return

  viewCookie.value = [...viewCookie.value, id]
  try {
    await $fetch(`/api/news/${id}/view`, { method: 'PUT' })
    // useFetch 의 data 는 얕은 반응형이라 객체를 통째로 교체해야 화면이 갱신된다
    news.value = { ...news.value!, viewCnt: news.value!.viewCnt + 1 }
  }
  catch {}
})

// 좋아요 — 이미 좋아요한 글이면 하트를 끝 프레임으로
function onAnimationLoaded(_: any) {
  if (news.value?.isLiked) {
    const duration = lottie.value.getDuration()
    lottie.value.goToAndStop(duration)
  }
}

async function onClickLikeButton() {
  if (!news.value)
    return

  if (!isLoggedIn.value) {
    showLoginPopup()
    return
  }

  // 후기와 같이 하트 애니메이션만 먼저 움직이고, 서버에 요청한 뒤 상세를 다시 조회해서 숫자를 갱신한다
  if (news.value.isLiked)
    lottie.value?.goToAndStop(0)
  else
    lottie.value?.goToAndPlay(20)

  try {
    await $fetch(`/api/news/${news.value.id}/like`, { method: 'PUT' })
    await refresh()
  }
  catch (e: any) {
    // 실패하면 하트를 원래 상태로 되돌린다
    if (news.value?.isLiked)
      lottie.value?.goToAndStop(lottie.value.getDuration())
    else
      lottie.value?.goToAndStop(0)

    if (e?.statusCode === 401)
      showLoginPopup()
    else
      console.error('좋아요 처리 실패', e)
  }
}
</script>

<style scoped>
.photo-slide .item{display:flex; justify-content:center}
.photo-slide .item img{max-width:100%; height:auto}
</style>
